import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Plus,
  Trash2,
  Edit2,
  Save,
  X,
  ChevronRight,
  ChevronDown,
  Play,
  Calendar,
  Eye,
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useData } from '../contexts/DataContext';
import { useNotification } from '../contexts/NotificationContext';
import { ExerciseCard } from './routine-editor/ExerciseCard';
import { ExercisePickerDrawer } from './routine-editor/ExercisePickerDrawer';
import { ExerciseDemoButton } from './ExerciseDemoButton';
import { TrainingPlanEditor } from './TrainingPlanEditor';
import { TrainingPlanBanner } from './TrainingPlanBanner';
import {
  getTrainingPlanWeekNumber,
  getWeekEndDate,
  getWeekStartDate,
  parseLocalDate,
} from '../utils/dateUtils';
import type { Routine, TrainingPlan } from '../types';

// Reusable carousel hook
function useCarousel() {
  const ref = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const handleScroll = useCallback(() => {
    const carousel = ref.current;
    if (!carousel) return;
    const cards = Array.from(
      carousel.querySelectorAll<HTMLElement>('[data-routine-card="true"]')
    );
    if (!cards.length) return;
    const viewportCenter =
      carousel.getBoundingClientRect().left + carousel.clientWidth / 2;
    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;
    cards.forEach((card, index) => {
      const cardRect = card.getBoundingClientRect();
      const cardCenter = cardRect.left + cardRect.width / 2;
      const distance = Math.abs(cardCenter - viewportCenter);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });
    setActiveIndex(closestIndex);
  }, []);

  const scrollTo = useCallback((index: number) => {
    const carousel = ref.current;
    if (!carousel) return;
    const cards = Array.from(
      carousel.querySelectorAll<HTMLElement>('[data-routine-card="true"]')
    );
    if (!cards.length) return;
    const nextIndex = Math.max(0, Math.min(cards.length - 1, index));
    cards[nextIndex]?.scrollIntoView({
      behavior: 'smooth',
      inline: 'center',
      block: 'nearest',
    });
    setActiveIndex(nextIndex);
  }, []);

  return { ref, activeIndex, setActiveIndex, handleScroll, scrollTo };
}

// Routine card component
function RoutineCard({
  routine,
  onEdit,
  onDelete,
  onStart,
  planDayLabel,
  isLinkedToPlan,
}: {
  routine: Routine;
  onEdit: () => void;
  onDelete: () => void;
  onStart: () => void;
  planDayLabel?: string;
  isLinkedToPlan?: boolean;
}) {
  const { darkMode, theme } = useTheme();
  const linkedSurface = darkMode
    ? 'border-[#5a3925] bg-[linear-gradient(180deg,rgba(36,29,22,0.95),rgba(24,20,16,0.98))]'
    : 'border-[#e5c4af] bg-[linear-gradient(180deg,rgba(255,249,243,0.96),rgba(247,238,228,0.98))]';
  const defaultSurface = darkMode
    ? 'border-stone-800 bg-[#1c1814]'
    : 'border-white/80 bg-white/78';

  return (
    <article
      data-routine-card="true"
      className={`lift-card animate-rise-in snap-start shrink-0 w-full lg:w-[calc((100%-2rem)/3)] rounded-[28px] border p-4 sm:p-6 shadow-sm flex flex-col min-h-[20rem] ${
        isLinkedToPlan ? linkedSurface : defaultSurface
      }`}
    >
      {planDayLabel && (
        <div className='mb-4'>
          <span className={`rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] ${
            darkMode ? 'bg-[#342419] text-[#f2c7ae]' : 'bg-[#f2ded2] text-[#8e3f1d]'
          }`}>
            {planDayLabel}
          </span>
        </div>
      )}
      <div className="flex items-start justify-between mb-4 gap-2">
        <div className="flex-1 min-w-0">
          <h3 className="text-xl sm:text-2xl font-black tracking-[-0.04em] mb-1 truncate">
            {routine.name}
          </h3>
          <p className={`${theme.textSecondary} text-sm`}>
            {routine.exercises.length} ejercicios · {routine.exercises.reduce((total, exercise) => total + exercise.sets, 0)} series
          </p>
        </div>
        <div className="flex gap-1 sm:gap-2 flex-shrink-0">
          <button
            onClick={onEdit}
            className={`pressable p-2 rounded-2xl ${theme.hover} touch-manipulation`}
            aria-label="Editar rutina"
          >
            <Edit2 className="w-5 h-5" />
          </button>
          <button
            onClick={onDelete}
            className={`pressable p-2 rounded-2xl ${
              isLinkedToPlan ? 'opacity-40 cursor-not-allowed' : 'hover:bg-red-600 active:bg-red-700 hover:text-white'
            } text-red-500 touch-manipulation`}
            aria-label="Eliminar rutina"
          >
            <Trash2 className="w-5 h-5" />
          </button>
        </div>
      </div>
      <div className="space-y-2 mb-5 pr-1 overflow-y-auto max-h-[12.5rem] routine-card-scroll">
        {routine.exercises.map((ex, i) => (
          <div
            key={i}
            className={`flex justify-between rounded-2xl px-3 py-2 text-sm ${
              darkMode ? 'bg-[#17130f]' : 'bg-stone-50/90'
            }`}
          >
            <div className="flex items-center gap-1.5 min-w-0">
              <span className="truncate font-medium">{ex.name}</span>
              <ExerciseDemoButton name={ex.name} iconClassName="w-4 h-4" />
            </div>
            <span className={`flex-shrink-0 ${theme.textSecondary}`}>
              {ex.sets} × {ex.reps}
            </span>
          </div>
        ))}
      </div>
      <button
        onClick={onStart}
        aria-label={`Entrenar ${routine.name}`}
        className="pressable w-full mt-auto py-3 rounded-[20px] bg-gradient-to-r from-[#b4532a] to-[#d97745] text-white font-semibold hover:brightness-105 touch-manipulation text-sm sm:text-base"
      >
        Entrenar
      </button>
    </article>
  );
}

// Carousel of routine cards
function RoutineCarousel({
  routines,
  label,
  onEditRoutine,
  onDeleteRoutine,
  onStartWorkout,
  planDays,
  isRoutineLinkedToPlan,
}: {
  routines: Routine[];
  label: string;
  onEditRoutine: (routine: Routine) => void;
  onDeleteRoutine: (routine: Routine) => void;
  onStartWorkout: (routine: Routine) => void;
  planDays?: { routine_id: string; day_number: number; label?: string }[];
  isRoutineLinkedToPlan?: (routineId: string) => boolean;
}) {
  const { theme } = useTheme();
  const carousel = useCarousel();

  useEffect(() => {
    if (routines.length === 0) {
      carousel.setActiveIndex(0);
      return;
    }
    carousel.setActiveIndex((current) =>
      Math.min(current, routines.length - 1)
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routines.length]);

  if (routines.length === 0) return null;

  return (
    <div className="relative">
      <div className="mb-3 flex items-center justify-between px-1">
        <p
          className={`text-[11px] sm:text-xs tracking-[0.26em] uppercase font-semibold ${theme.textSecondary}`}
        >
          {label}
        </p>
        {routines.length > 0 && (
          <span className={`text-xs ${theme.textSecondary}`}>
            {carousel.activeIndex + 1}/{routines.length}
          </span>
        )}
      </div>

      <div
        ref={carousel.ref}
        onScroll={carousel.handleScroll}
        className="routine-carousel flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth px-1 pb-2 pt-1"
        aria-label={`Carrusel: ${label}`}
      >
        {routines.map((routine) => {
          const planDay = planDays?.find(d => d.routine_id === routine.id);
          const dayLabel = planDay
            ? `Día ${planDay.day_number}${planDay.label ? ` — ${planDay.label}` : ''}`
            : undefined;
          return (
            <RoutineCard
              key={routine.id ?? routine.name}
              routine={routine}
              planDayLabel={dayLabel}
              isLinkedToPlan={isRoutineLinkedToPlan?.(routine.id!) ?? false}
              onEdit={() => onEditRoutine(routine)}
              onDelete={() => onDeleteRoutine(routine)}
              onStart={() => onStartWorkout(routine)}
            />
          );
        })}
      </div>

      {routines.length > 1 && (
        <div className="mt-3 flex items-center justify-center gap-2">
          {routines.map((routine, index) => (
            <button
              key={`dot-${routine.id ?? routine.name}-${index}`}
              type="button"
              onClick={() => carousel.scrollTo(index)}
              className={`h-2.5 rounded-full transition-all ${
                carousel.activeIndex === index
                  ? 'w-8 bg-[#d97745]'
                  : `w-2.5 ${theme.border} border ${theme.card}`
              }`}
              aria-label={`Ir a rutina ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Skeleton carousel (loading state)
function SkeletonCarousel() {
  const { darkMode, theme } = useTheme();
  return (
    <div className="relative">
      <div className="mb-2 px-1">
        <p className={`text-[11px] sm:text-xs tracking-[0.22em] uppercase font-semibold ${theme.textSecondary}`}>
          Mis rutinas
        </p>
      </div>
      <div className="flex snap-x snap-mandatory gap-3 sm:gap-4 overflow-x-auto px-1 pb-2 pt-1">
        {Array.from({ length: 3 }).map((_, index) => (
          <div
            key={`routine-skeleton-${index}`}
            className={`snap-start shrink-0 w-full lg:w-[calc((100%-2rem)/3)] p-4 sm:p-6 rounded-[28px] border flex flex-col min-h-[20rem] opacity-70 ${
              darkMode ? 'border-stone-800 bg-[#1c1814]' : 'border-white/80 bg-white/75'
            }`}
          >
            <div className="animate-pulse space-y-3">
              <div className="h-5 w-2/3 rounded bg-slate-500/20" />
              <div className="h-4 w-1/3 rounded bg-slate-500/15" />
              <div className="space-y-2 pt-2">
                <div className="h-3 w-full rounded bg-slate-500/15" />
                <div className="h-3 w-5/6 rounded bg-slate-500/15" />
                <div className="h-3 w-2/3 rounded bg-slate-500/15" />
              </div>
              <div className="h-10 w-full rounded-xl bg-slate-500/20" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Past plans section
function PastPlansSection({
  plans,
  hasActivePlan,
  onView,
  onReactivate,
  onDelete,
}: {
  plans: TrainingPlan[];
  hasActivePlan: boolean;
  onView: (plan: TrainingPlan) => void;
  onReactivate: (plan: TrainingPlan) => void;
  onDelete: (plan: TrainingPlan) => void;
}) {
  const { theme } = useTheme();
  const [expanded, setExpanded] = useState(false);

  if (plans.length === 0) return null;

  return (
    <div className="px-1">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className={`pressable w-full flex items-center gap-2 py-2 text-sm ${theme.textSecondary}`}
      >
        <ChevronDown className={`w-4 h-4 transition-transform ${expanded ? 'rotate-0' : '-rotate-90'}`} />
        Planes anteriores ({plans.length})
      </button>

      {expanded && (
        <div className="space-y-2 mt-1">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`lift-card rounded-[22px] border ${theme.border} ${theme.card} p-3 flex items-center justify-between`}
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{plan.name}</p>
                <div className={`flex items-center gap-2 text-xs ${theme.textSecondary}`}>
                  {plan.start_date && (
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {parseLocalDate(plan.start_date).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: '2-digit' })}
                    </span>
                  )}
                  <span
                    className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${
                      plan.status === 'completed'
                        ? 'bg-green-500/20 text-green-500'
                        : 'bg-orange-500/20 text-orange-500'
                    }`}
                  >
                    {plan.status === 'completed' ? 'Completado' : 'Abandonado'}
                  </span>
                </div>
              </div>
              <div className="flex gap-1 shrink-0">
                <button
                  onClick={() => onView(plan)}
                  className={`pressable p-2 rounded-xl ${theme.hover}`}
                  aria-label="Ver detalles del plan"
                >
                  <Eye className="w-4 h-4" />
                </button>
                {!hasActivePlan && (
                  <button
                    onClick={() => onReactivate(plan)}
                    className="pressable p-2 rounded-xl text-green-500 hover:bg-green-600 hover:text-white"
                    aria-label="Reactivar plan"
                  >
                    <Play className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={() => onDelete(plan)}
                  className="pressable p-2 rounded-xl text-red-500 hover:bg-red-600 hover:text-white"
                  aria-label="Eliminar plan"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function RoutinesTab() {
  const { theme } = useTheme();
  const { showNotification } = useNotification();
  const {
    routines,
    workouts,
    isDemoMode,
    editingRoutine,
    editingPlan,
    exerciseCatalog,
    trainingPlans,
    setEditingRoutine,
    setEditingPlan,
    createRoutine,
    addExerciseFromCatalog,
    updateExercise,
    removeExercise,
    saveRoutine,
    deleteRoutine,
    startWorkout,
    ensureExerciseEntry,
    createPlan,
    activatePlan,
    completePlan,
    abandonPlan,
    deletePlan,
    getActivePlan,
    getActivePlanContext,
    getUnplannedRoutines,
  } = useData();

  const [showPicker, setShowPicker] = useState(false);

  const activePlan = getActivePlan();

  // Compute week number for active plan
  const rawWeek = useMemo(() => {
    if (!activePlan?.start_date) return 1;
    const startDate = parseLocalDate(activePlan.start_date);
    return getTrainingPlanWeekNumber(startDate);
  }, [activePlan]);

  const activePlanWeek = activePlan ? Math.min(rawWeek, activePlan.duration_weeks) : 1;
  const isExpired = activePlan ? rawWeek > activePlan.duration_weeks : false;

  const activePlanDayProgress = useMemo(() => {
    if (!activePlan) return [];

    const sortedDays = activePlan.days
      .slice()
      .sort((a, b) => a.day_number - b.day_number);

    if (!activePlan.start_date) {
      return sortedDays.map((day) => ({
        dayNumber: day.day_number,
        label: day.label,
        completed: false,
      }));
    }

    const startDate = parseLocalDate(activePlan.start_date);
    const displayedWeekStart = getWeekStartDate(startDate);
    displayedWeekStart.setDate(
      displayedWeekStart.getDate() + (activePlanWeek - 1) * 7
    );
    const weekStart = displayedWeekStart < startDate ? startDate : displayedWeekStart;
    const weekEnd = getWeekEndDate(displayedWeekStart);

    const workoutsThisWeek = workouts.filter((workout) => {
      if (!activePlan.id || workout.training_plan_id !== activePlan.id) return false;
      const workoutDate = parseLocalDate(workout.date);
      return workoutDate >= weekStart && workoutDate <= weekEnd;
    });

    const completedByRoutine = new Map<string, number>();
    workoutsThisWeek.forEach((workout) => {
      const key = workout.routine_id ? String(workout.routine_id) : '';
      if (!key) return;
      completedByRoutine.set(key, (completedByRoutine.get(key) ?? 0) + 1);
    });

    return sortedDays.map((day) => {
      const key = String(day.routine_id);
      const remainingCount = completedByRoutine.get(key) ?? 0;
      const completed = remainingCount > 0;
      if (completed) {
        completedByRoutine.set(key, remainingCount - 1);
      }
      return {
        dayNumber: day.day_number,
        label: day.label,
        completed,
      };
    });
  }, [activePlan, activePlanWeek, workouts]);

  const activePlanHistory = useMemo(() => {
    if (!activePlan?.id) return [];
    return workouts
      .filter((workout) => workout.training_plan_id === activePlan.id)
      .slice()
      .sort(
        (a, b) =>
          parseLocalDate(b.date).getTime() - parseLocalDate(a.date).getTime()
      )
      .map((workout) => ({
        id: workout.id ?? `${workout.date}-${workout.routine_name}`,
        date: workout.date,
        routineName: workout.routine_name,
        exerciseCount: workout.exercises.length,
      }));
  }, [activePlan?.id, workouts]);

  // Sort and split routines
  const sortedRoutines = useMemo(
    () =>
      [...routines].sort((a, b) =>
        a.name.localeCompare(b.name, 'es', { sensitivity: 'base' })
      ),
    [routines]
  );

  const planRoutines = useMemo(() => {
    if (!activePlan) return [];
    return activePlan.days
      .slice()
      .sort((a, b) => a.day_number - b.day_number)
      .map(day => sortedRoutines.find(r => r.id === day.routine_id))
      .filter((r): r is Routine => !!r);
  }, [activePlan, sortedRoutines]);

  const unplannedRoutines = useMemo(
    () => getUnplannedRoutines(sortedRoutines),
    [getUnplannedRoutines, sortedRoutines]
  );

  const pastPlans = useMemo(
    () => trainingPlans.filter(p => p.status === 'completed' || p.status === 'abandoned'),
    [trainingPlans]
  );

  const draftPlans = useMemo(
    () => trainingPlans.filter(p => p.status === 'draft'),
    [trainingPlans]
  );

  // Keyboard handler for editing routine
  useEffect(() => {
    if (!editingRoutine) {
      setShowPicker(false);
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (showPicker) {
          setShowPicker(false);
        } else {
          setEditingRoutine(null);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [editingRoutine, showPicker, setEditingRoutine]);

  const handleSelectFromPicker = (entry: { id: string; name: string }) => {
    addExerciseFromCatalog(entry);
    setShowPicker(false);
  };

  const handleCreateCustom = async (name: string) => {
    try {
      const entry = await ensureExerciseEntry(name);
      return entry;
    } catch (error) {
      console.error('Error creando ejercicio:', error);
      showNotification('No se pudo crear el ejercicio', 'error');
      return null;
    }
  };

  const isRoutineLinkedToPlan = useCallback((routineId: string): boolean => {
    return trainingPlans.some(p => p.days.some(d => d.routine_id === routineId));
  }, [trainingPlans]);

  const handleDeleteRoutine = (routine: Routine) => {
    // Block deletion if routine is referenced in any plan (DB uses ON DELETE RESTRICT)
    const linkedPlans = trainingPlans.filter(p =>
      p.days.some(d => d.routine_id === routine.id)
    );
    const linkedPlan =
      linkedPlans.find(p => p.status === 'active') ??
      linkedPlans.find(p => p.status === 'draft') ??
      linkedPlans[0];
    if (linkedPlan) {
      const isPastPlan = linkedPlan.status === 'completed' || linkedPlan.status === 'abandoned';
      showNotification(
        isPastPlan
          ? `Está en el plan anterior "${linkedPlan.name}". Eliminá ese plan primero.`
          : `Quitala del plan "${linkedPlan.name}" para eliminarla`,
        'error'
      );
      return;
    }
    deleteRoutine(routine.id!);
  };

  const handleStartPlanWorkout = (routine: Routine) => {
    const planContext = getActivePlanContext(routine.id!);
    startWorkout(routine, planContext);
  };

  const handleCreatePlan = () => {
    if (isDemoMode) {
      showNotification('Modo demo: creá una rutina primero', 'error');
      createRoutine();
      return;
    }

    if (sortedRoutines.length === 0) {
      showNotification('Creá una rutina primero', 'error');
      createRoutine();
      return;
    }
    createPlan();
  };

  const handleEditPlan = (plan: TrainingPlan) => {
    const isHistorical = plan.status === 'completed' || plan.status === 'abandoned';
    setEditingPlan({
      id: plan.id,
      name: plan.name,
      days_per_week: plan.days_per_week,
      duration_weeks: plan.duration_weeks,
      readOnly: isHistorical,
      days: plan.days.map(d => ({
        day_number: d.day_number,
        routine_id: d.routine_id,
        label: d.label,
      })),
    });
  };

  const addRoutineButtonClass = `w-full rounded-2xl border ${theme.border} ${theme.card} px-3 py-2.5 flex items-center gap-3 text-left ${theme.hover} transition-colors`;
  const addRoutineCircleClass = `w-10 h-10 rounded-full border ${theme.border} flex items-center justify-center ${theme.textSecondary} shrink-0`;
  const addRoutineLabelClass = 'flex-1 min-w-0 text-lg font-medium';
  const addRoutineChevronClass = `w-5 h-5 ${theme.textSecondary} shrink-0`;

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* ── Editing routine modal ──────────────────────────────────── */}
      {editingRoutine && (
        <div
          className="fixed inset-x-0 top-0 z-50 flex h-[100dvh] items-center justify-center px-3 pt-[calc(env(safe-area-inset-top)+0.75rem)] pb-[calc(env(safe-area-inset-bottom)+0.75rem)] sm:p-6"
          role="dialog"
          aria-modal="true"
          onClick={() => setEditingRoutine(null)}
        >
          <div className="absolute inset-0 bg-black/50" />
          <div
            className={`relative w-full max-w-2xl max-h-[calc(100dvh-1.5rem-env(safe-area-inset-top)-env(safe-area-inset-bottom))] sm:max-h-[85vh] overflow-y-auto rounded-lg ${theme.card} border ${theme.border} shadow-xl`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 sm:p-6 space-y-3 sm:space-y-4 pb-[calc(env(safe-area-inset-bottom)+9rem)] sm:pb-40">
              <input
                type="text"
                placeholder="Nombre de la rutina"
                value={editingRoutine.name}
                onChange={(e) =>
                  setEditingRoutine({
                    ...editingRoutine,
                    name: e.target.value.slice(0, 50),
                  })
                }
                maxLength={50}
                className={`w-full px-4 py-3 sm:py-2 rounded-lg border ${theme.input} text-base sm:text-sm`}
              />

              <div className="space-y-2">
                {editingRoutine.exercises.map((exercise: any, index: number) => (
                  <ExerciseCard
                    key={index}
                    name={exercise.name}
                    sets={exercise.sets}
                    reps={exercise.reps}
                    index={index}
                    updateExercise={updateExercise}
                    removeExercise={removeExercise}
                  />
                ))}
              </div>

              <button
                type="button"
                onClick={() => setShowPicker(true)}
                className={`w-full py-3 sm:py-2 rounded-lg border ${theme.border} ${theme.hover} active:opacity-80 flex items-center justify-center gap-2 touch-manipulation text-sm sm:text-base`}
              >
                <Plus className="w-4 h-4" />
                Agregar ejercicio
              </button>
            </div>

            <div
              className={`sticky bottom-0 left-0 right-0 z-20 border-t ${theme.border} ${theme.card} backdrop-blur-md px-4 pt-4 pb-[calc(env(safe-area-inset-bottom)+1rem)] sm:p-6`}
            >
              <div className="flex flex-row gap-2">
                <button
                  onClick={saveRoutine}
                  className="flex-1 py-3 rounded-lg bg-green-600 text-white font-medium hover:bg-green-700 active:bg-green-800 flex items-center justify-center gap-2 touch-manipulation text-sm sm:text-base"
                >
                  <Save className="w-5 h-5" />
                  Guardar
                </button>
                <button
                  onClick={() => setEditingRoutine(null)}
                  className="flex-1 py-3 rounded-lg bg-red-600 text-white font-medium hover:bg-red-700 active:bg-red-800 flex items-center justify-center gap-2 touch-manipulation text-sm sm:text-base"
                >
                  <X className="w-5 h-5" />
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Exercise Picker Drawer */}
      <ExercisePickerDrawer
        isOpen={showPicker}
        onClose={() => setShowPicker(false)}
        exerciseCatalog={exerciseCatalog}
        onSelect={handleSelectFromPicker}
        onCreateCustom={handleCreateCustom}
      />

      {/* Training Plan Editor Modal */}
      <TrainingPlanEditor />

      {/* ── Active Plan ──────────────────────────────────── */}
      {activePlan && (
        <>
          <div className="px-1">
            <TrainingPlanBanner
              plan={activePlan}
              weekNumber={activePlanWeek}
              isExpired={isExpired}
              dayProgress={activePlanDayProgress}
              planHistory={activePlanHistory}
              onEdit={() => handleEditPlan(activePlan)}
              onComplete={() => completePlan(activePlan.id!)}
              onAbandon={() => abandonPlan(activePlan.id!)}
            />
          </div>

          {/* Plan routines carousel */}
          <RoutineCarousel
            routines={planRoutines}
            label="Rutinas del plan"
            planDays={activePlan.days}
            isRoutineLinkedToPlan={isRoutineLinkedToPlan}
            onEditRoutine={(r) => setEditingRoutine(r as any)}
            onDeleteRoutine={handleDeleteRoutine}
            onStartWorkout={handleStartPlanWorkout}
          />

          {/* Unplanned routines */}
          {unplannedRoutines.length > 0 && (
            <>
              <div className={`mx-1 border-t ${theme.border}`} />
              <RoutineCarousel
                routines={unplannedRoutines}
                label="Rutinas sin plan"
                isRoutineLinkedToPlan={isRoutineLinkedToPlan}
                onEditRoutine={(r) => setEditingRoutine(r as any)}
                onDeleteRoutine={handleDeleteRoutine}
                onStartWorkout={(r) => startWorkout(r)}
              />
            </>
          )}
        </>
      )}

      {/* ── No Active Plan ──────────────────────────────── */}
      {!activePlan && (
        <>
          {!editingRoutine && !editingPlan && (
            <div className="px-1 space-y-3">
              <button
                type="button"
                onClick={handleCreatePlan}
                className={`${addRoutineButtonClass} pressable lift-card rounded-[24px]`}
                aria-label="Crear plan de entrenamiento"
              >
                <span className={addRoutineCircleClass}>
                  <Calendar className="w-5 h-5" />
                </span>
                <span className={addRoutineLabelClass}>Nuevo Plan</span>
                <ChevronRight className={addRoutineChevronClass} />
              </button>
            </div>
          )}

          {!editingRoutine && !editingPlan && (
            <div className="px-1">
              <button
                type="button"
                onClick={createRoutine}
                className={`${addRoutineButtonClass} pressable lift-card rounded-[24px]`}
                aria-label="Agregar rutina"
              >
                <span className={addRoutineCircleClass}>
                  <Plus className="w-5 h-5" />
                </span>
                <span className={addRoutineLabelClass}>Nueva Rutina</span>
                <ChevronRight className={addRoutineChevronClass} />
              </button>
            </div>
          )}

          {draftPlans.length > 0 && (
            <div className="px-1 space-y-2">
              {draftPlans.map((plan) => (
                <div
                  key={plan.id}
                  className={`lift-card rounded-[24px] border ${theme.border} ${theme.card} p-4 flex items-center justify-between`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{plan.name || 'Plan sin nombre'}</p>
                    <p className={`text-xs ${theme.textSecondary}`}>
                      Borrador · {plan.days_per_week} días/sem · {plan.duration_weeks} sem
                    </p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button
                      onClick={() => handleEditPlan(plan)}
                      className={`pressable p-2 rounded-xl ${theme.hover}`}
                      aria-label="Editar plan"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => activatePlan(plan.id!)}
                      className="pressable p-2 rounded-xl text-green-500 hover:bg-green-600 hover:text-white"
                      aria-label="Activar plan"
                    >
                      <Play className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => deletePlan(plan.id!)}
                      className="pressable p-2 rounded-xl text-red-500 hover:bg-red-600 hover:text-white"
                      aria-label="Eliminar plan"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* All routines carousel */}
          {sortedRoutines.length === 0 ? (
            <SkeletonCarousel />
          ) : (
            <RoutineCarousel
              routines={sortedRoutines}
              label="Mis rutinas"
              isRoutineLinkedToPlan={isRoutineLinkedToPlan}
              onEditRoutine={(r) => setEditingRoutine(r as any)}
              onDeleteRoutine={handleDeleteRoutine}
              onStartWorkout={(r) => startWorkout(r)}
            />
          )}
        </>
      )}

      {/* Past plans */}
      <PastPlansSection
        plans={pastPlans}
        hasActivePlan={!!activePlan}
        onView={handleEditPlan}
        onReactivate={(plan) => activatePlan(plan.id!)}
        onDelete={(plan) => deletePlan(plan.id!)}
      />
    </div>
  );
}
